// src/components/layout/DetailsHosted.tsx
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
    Container,
    Button,
    Heading,
    Text,
    Image,
    Box,
    Flex,
    VStack,
    Table,
    Tbody,
    Tr,
    Td,
    Input,
    Checkbox,
    Badge,
    Spinner,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    SimpleGrid,
} from "@chakra-ui/react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useHostedOrdersAndTickets } from "../../hooks/useHostedOrdersAndTickets.ts";
import {useTranslation} from "react-i18next";

const DetailsHosted: React.FC = () => {
    const { t } = useTranslation();
    const { eventId } = useParams<{ eventId: string }>();
    const { eventDetails, ordersWithInfos, isLoading, error } =
        useHostedOrdersAndTickets(eventId!);

    const ordersWithTickets = ordersWithInfos.map(({ orderView, orderInfoView }) => ({
        order: orderView,
        tickets: orderInfoView,
    }));

    const [searchQuery, setSearchQuery] = useState("");
    const [orderDateFilter, setOrderDateFilter] = useState("");
    const [showOnlyCheckedIn, setShowOnlyCheckedIn] = useState(false);

    if (error) {
        return (
            <Flex align="center" justify="center" h="100vh">
                <Text color="red.500">Error loading data: {error.message}</Text>
            </Flex>
        );
    }

    if (isLoading) {
        return (
            <Flex align="center" justify="center" h="100vh">
                <Spinner size="xl" />
            </Flex>
        );
    }

    if (!eventDetails) {
        return (
            <Flex align="center" justify="center" h="100vh">
                <Text>No event found.</Text>
            </Flex>
        );
    }

    // filter orders & tickets
    const filteredOrders = ordersWithTickets
        .map(({ order, tickets }) => ({
            order,
            tickets: showOnlyCheckedIn
                ? tickets.filter((t) => t.qrCheckInCount > 0)
                : tickets,
        }))
        .filter(({ order, tickets }) => {
            const q = searchQuery.toLowerCase();

            // coalesce null → "" before lowercasing:
            const txId = (order.transactionId ?? "").toLowerCase();
            const matchesTxId = txId.includes(q);

            const matchesInTickets = tickets.some((t) => {
                const haystack = [
                    t.id,
                    t.email,
                    t.name,
                    t.surname,
                ]
                    .map((v) => v ?? "")
                    .join(" ")
                    .toLowerCase();
                return haystack.includes(q);
            });

            const matchesDate =
                !orderDateFilter || order.orderDate.startsWith(orderDateFilter);

            return (matchesTxId || matchesInTickets) && matchesDate && tickets.length > 0;
        });

    // summary stats
    const stats = {
        totalOrders: ordersWithTickets.length,
        totalTickets: ordersWithTickets.reduce((sum, o) => sum + o.tickets.length, 0),
        revenue: ordersWithTickets.reduce(
            (sum, o) => sum + o.tickets.reduce((s, t) => s + t.price, 0),
            0
        ),
        checkedIn: ordersWithTickets.reduce(
            (sum, o) => sum + o.tickets.filter((t) => t.qrCheckInCount > 0).length,
            0
        ),
        freeTickets: ordersWithTickets.some((o) =>
            o.tickets.some((t) => t.price === 0)
        ),
    };

    // prepare export data
    const exportData = ordersWithTickets.flatMap(({ order, tickets }, idx) =>
        tickets.map((t, i) => ({
            "#": i === 0 ? idx + 1 : "",
            "Order ID": i === 0 ? order.id : "",
            "Transaction ID": i === 0 ? order.transactionId : "",
            "Payment Source": i === 0 ? order.paymentSource : "",
            "Order Date": i === 0 ? order.orderDate : "",
            "Ticket ID": t.id,
            Email: t.email,
            "Full Name": `${t.name} ${t.surname}`,
            Price: t.price,
            "Checked-In": t.qrCheckInCount > 0 ? "Yes" : "No",
            "Last Check-In": t.checkInDateTime ?? "",
            Contact: t.contact_number,
        }))
    );

    const handleExportExcel = () => {
        const wb = XLSX.utils.book_new();
        const ws1 = XLSX.utils.json_to_sheet(exportData);
        XLSX.utils.book_append_sheet(wb, ws1, "Orders & Tickets");

        const statsData = [
            { Metric: "Total Orders", Value: stats.totalOrders },
            { Metric: "Total Tickets", Value: stats.totalTickets },
            { Metric: "Revenue", Value: stats.revenue },
            { Metric: "Checked-In", Value: stats.checkedIn },
            { Metric: "Free Tickets", Value: stats.freeTickets ? "Yes" : "No" },
        ];
        const ws2 = XLSX.utils.json_to_sheet(statsData);
        XLSX.utils.book_append_sheet(wb, ws2, "Statistics");

        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(
            new Blob([wbout], { type: "application/octet-stream" }),
            "event_export.xlsx"
        );
    };

    return (
        <Container maxW="800px" pt="10%" pb="10%">
            <VStack align="stretch" spacing={6}>
                {/* Event Title */}
                <Box
                    w="100%"
                    h={{ base: "500px", md: "500px" }}
                    position="relative"
                    backgroundImage={`url(https://api.eventeam.az/images/${eventDetails.coverPhoto})`}
                    backgroundSize="contain"
                    backgroundPosition="center"
                    backgroundRepeat="no-repeat"
                >


                </Box>


                <Heading>{eventDetails.eventName}</Heading>


                {/* Event Details */}
                <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
                    <Table variant="simple">
                        <Tbody>
                            <Tr>
                                <Td fontWeight="bold">{t("Event ID") /* no key; left as-is */}</Td>
                                <Td>{eventDetails.idEvent}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">{t("Start Date")}</Td>
                                <Td>{eventDetails.startDate}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">{t("End Date")}</Td>
                                <Td>{eventDetails.endDate ?? "—"}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">{t("Start Time")}</Td>
                                <Td>{eventDetails.startTime}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">{t("End Time")}</Td>
                                <Td>{eventDetails.endTime ?? "—"}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">{t("Minutes") /* from translation */}</Td>
                                <Td>{eventDetails.duration ?? "—"}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">{t("Description")}</Td>
                                <Td>{eventDetails.description ?? "—"}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">{t("Address") /* no key; left as-is */}</Td>
                                <Td>{eventDetails.address ?? "—"}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">{t("Mobile")}</Td>
                                <Td>{eventDetails.mobileNumber ?? "—"}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">{t("Categories")}</Td>
                                <Td>
                                    {eventDetails.listOfCategories.map((c) => (
                                        <Box key={c.keyword} mb={1}>
                                            <Text>
                                                <strong>{t("Name")}:</strong> {c.name}
                                            </Text>
                                            <Text>
                                                <strong>{t("Keyword")}:</strong> {c.keyword}
                                            </Text>
                                            <Text>
                                                <strong>{t("Title")}:</strong> {c.title ?? "—"}
                                            </Text>
                                            <Text>
                                                <strong>{t("Body")}:</strong> {c.body ?? "—"}
                                            </Text>
                                        </Box>
                                    ))}
                                </Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">{t("View Count") /* no key */}</Td>
                                <Td>
                                    <Badge colorScheme="green">{eventDetails.viewCount}</Badge>
                                </Td>
                            </Tr>

                        </Tbody>
                    </Table>
                </Box>

                {/* Orders & Tickets */}

                {/* Orders & Tickets */}
                <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
                    <Text fontSize="xl" mb={4}>
                        Orders & Tickets
                    </Text>

                    <Flex gap={2} mb={4} wrap="wrap">
                        <Input
                            placeholder="Search Order/Ticket/Email"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            w={{ base: "100%", sm: "200px" }}
                        />
                        <Input
                            type="date"
                            value={orderDateFilter}
                            onChange={(e) => setOrderDateFilter(e.target.value)}
                            w={{ base: "100%", sm: "150px" }}
                        />
                        <Checkbox
                            isChecked={showOnlyCheckedIn}
                            onChange={(e) => setShowOnlyCheckedIn(e.target.checked)}
                        >
                            Only Checked-In
                        </Checkbox>
                        <Button
                            onClick={handleExportExcel}
                            colorScheme="green"
                            ml="auto"
                            size="sm"
                        >
                            Export Excel
                        </Button>
                    </Flex>

                    <Box mb={4}>
                        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 6 }} spacing={4}>
                            <Box>
                                <Text>Total Orders</Text>
                                <Text fontSize="lg">{stats.totalOrders}</Text>
                            </Box>
                            <Box>
                                <Text>Total Tickets</Text>
                                <Text fontSize="lg">{stats.totalTickets}</Text>
                            </Box>
                            <Box>
                                <Text>Revenue</Text>
                                <Text fontSize="lg">{stats.revenue} ₼</Text>
                            </Box>
                            <Box>
                                <Text>Checked-In</Text>
                                <Text fontSize="lg">{stats.checkedIn}</Text>
                            </Box>
                            <Box>
                                <Text>Free Tickets?</Text>
                                <Text fontSize="lg">
                                    {stats.freeTickets ? "✅ Yes" : "❌ No"}
                                </Text>
                            </Box>
                        </SimpleGrid>
                    </Box>

                    {filteredOrders.length === 0 ? (
                        <Text>No orders or tickets found.</Text>
                    ) : (
                        <Accordion allowMultiple>
                            {filteredOrders.map(({ order, tickets }) => (
                                <AccordionItem
                                    key={order.id}
                                    border="1px solid"
                                    borderColor="gray.200"
                                    mb={2}
                                    borderRadius="md"
                                >
                                    <AccordionButton _expanded={{ bg: "gray.100" }}>
                                        <Box flex="1" textAlign="left">
                                            {order.transactionId} — {order.paymentStatus} —{" "}
                                            {new Date(order.orderDate).toLocaleString()} —{" "}{order.paymentSource}
                                        </Box>
                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel pb={4}>
                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                            {tickets.map((t) => (
                                                <Box
                                                    key={t.id}
                                                    p={3}
                                                    borderWidth="1px"
                                                    borderRadius="md"
                                                >
                                                    <Text fontWeight="bold">
                                                        {t.name} {t.surname}
                                                    </Text>
                                                    <Text fontSize="sm">Email: {t.email}</Text>
                                                    <Text fontSize="sm">Price: {t.price} ₼</Text>
                                                    <Text fontSize="sm">
                                                        Checked-In: {t.qrCheckInCount > 0 ? "✅" : "❌"}
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.500">
                                                        Last Check-In: {t.checkInDateTime ?? "—"}
                                                    </Text>
                                                </Box>
                                            ))}
                                        </SimpleGrid>
                                    </AccordionPanel>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    )}
                </Box>
            </VStack>
        </Container>
    );
};

export default DetailsHosted;
